import {
  ZodTypeAny,
  ZodObject,
  ZodOptional,
  ZodNullable,
  ZodArray,
  ZodEffects,
  ZodTuple,
  ZodLazy,
  ZodDefault,
  ZodUnion,
  ZodIntersection,
} from 'zod';

/**
 * 주어진 Zod 스키마에서 특정 경로에 해당하는 하위 Zod 스키마를 추출합니다.
 *
 * @param schema 전체 Zod 스키마 객체입니다.
 * @param path 추출하고자 하는 하위 스키마의 경로 문자열입니다 (점(.)으로 구분).
 * @returns 경로에 해당하는 ZodTypeAny 또는 찾을 수 없는 경우 null을 반환합니다.
 */
export const extractFieldSchema = (schema: ZodTypeAny, path: string): ZodTypeAny | null => {
  const segments = path.split('.');
  let currentSchema: ZodTypeAny = schema;

  for (const segment of segments) {
    if (segment === undefined || segment === '') {
      continue;
    }

    currentSchema = unwrapSchema(currentSchema);

    if (currentSchema instanceof ZodObject) {
      if (!(segment in currentSchema.shape)) {
        return null;
      }
      currentSchema = currentSchema.shape[segment];
    } else if (currentSchema instanceof ZodArray && !isNaN(parseInt(segment, 10))) {
      currentSchema = currentSchema.element;
    } else if (currentSchema instanceof ZodTuple && !isNaN(parseInt(segment, 10))) {
      const index = parseInt(segment, 10);
      if (index >= 0 && index < currentSchema.items.length) {
        currentSchema = currentSchema.items[index];
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  return currentSchema;
};

export const unwrapSchema = (schema: ZodTypeAny): ZodTypeAny => {
  let current = schema;
  const visited = new Set();

  while (true) {
    if (visited.has(current)) break;
    visited.add(current);

    if (current instanceof ZodOptional || current instanceof ZodNullable) {
      current = current.unwrap();
    } else if (current instanceof ZodEffects && 'schema' in current._def) {
      current = current._def.schema;
    } else if (current instanceof ZodLazy) {
      current = current._def.getter();
    } else if (current instanceof ZodDefault) {
      current = current._def.innerType;
    } else if (current instanceof ZodUnion) {
      current = current.options[0];
    } else if (current instanceof ZodIntersection) {
      current = current._def.left;
    } else {
      break;
    }
  }

  return current;
};
